import { useState, memo } from "react";
import { Sparkles, Search, Briefcase, Target, ArrowRight } from "lucide-react";
import SmartAnalyzer from "./SmartAnalyzer";
import { SmartPortfolioAnalyzer, SmartFinderAnalyzer } from "./SmartPortfolioAnalyzer";

/**
 * AnalystTab — Smart Analyse Claude (3-prompts deep dive).
 * Replaces the legacy 3-LLM "Comité d'Experts" with focused, single-model
 * Claude Sonnet 4.5 analyses:
 *   1. Ticker deep dive   → Narrative + Fundamentals + Asymmetry (3 prompts // parallel)
 *   2. Portfolio diagnostic → Garder / Vendre / Risques / Action prioritaire
 *   3. Finder              → Trouver 2-4 idées selon des critères libres
 *
 * `initialTicker` lets external callers (e.g. Algo "Send to Committee") deep-link
 * straight into a ticker analysis. The internal `cleared` flag lets the user
 * close that analysis and start over without the prop forcing it back open.
 */
export default memo(function AnalystTab({ owner = "Helmi", portfolio = [], initialTicker = "" }) {
  const [mode, setMode] = useState(initialTicker ? "ticker" : "ticker");
  const [tickerInput, setTickerInput] = useState("");
  const [submittedTicker, setSubmittedTicker] = useState("");
  const [cleared, setCleared] = useState(false);

  // Effective ticker = user submission OR external deep link (until cleared)
  const effectiveTicker = submittedTicker || (cleared ? "" : (initialTicker || "").toUpperCase());

  const submitTicker = (raw) => {
    const t = (raw || "").trim().toUpperCase();
    if (!t) return;
    setSubmittedTicker(t);
    setCleared(false);
  };

  const closeAnalysis = () => {
    setSubmittedTicker("");
    setCleared(true);
  };

  const quickTickers = ["NVDA", "GOOGL", "ORCL", "OKLO", "MRVL", "OSCR", "CRDO", "LLY"];

  return (
    <div className="animate-fade-in" data-testid="expert-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-5">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
            <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-purple-300" />
            Smart Analyse <span className="text-purple-300">Claude</span>
          </h1>
          <p className="text-slate-400 mt-1 text-xs sm:text-sm">
            Deep dive 3-prompts : Narrative · Fundamentals · Asymmetry — propulsé par Claude Sonnet 4.5
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-2 text-xs text-slate-400 font-mono">
          <span className="w-2 h-2 rounded-full bg-purple-300 animate-pulse" />
          Anthropic · claude-sonnet-4-5
        </span>
      </div>

      {/* Mode tabs */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <ModeButton
          active={mode === "ticker"}
          onClick={() => setMode("ticker")}
          icon={Target}
          label="Analyse Ticker"
          sub="Deep dive 1 action"
          testId="mode-ticker"
        />
        <ModeButton
          active={mode === "portfolio"}
          onClick={() => setMode("portfolio")}
          icon={Briefcase}
          label="Mon Portefeuille"
          sub="Diagnostic global"
          testId="mode-portfolio"
        />
        <ModeButton
          active={mode === "finder"}
          onClick={() => setMode("finder")}
          icon={Search}
          label="Trouver une idée"
          sub="2-4 idées sur mesure"
          testId="mode-finder"
        />
      </div>

      {/* TICKER MODE */}
      {mode === "ticker" && (
        <div className="space-y-4">
          <div className="glass-card rounded-xl p-4 border border-purple-500/20">
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 block">
              Ticker à analyser
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && submitTicker(tickerInput)}
                placeholder="NVDA, OKLO, ASML.AS, MRVL..."
                className="flex-1 px-4 py-3 bg-slate-900/70 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-400/60 font-mono"
                data-testid="ticker-deepdive-input"
                autoFocus
              />
              <button
                onClick={() => submitTicker(tickerInput)}
                disabled={!tickerInput.trim()}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white font-bold text-sm rounded-xl shadow-glow flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="ticker-deepdive-submit"
              >
                <Sparkles className="w-4 h-4" />
                Deep Dive
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <span className="text-[10px] text-slate-500 mr-1 pt-1">Suggestions:</span>
              {quickTickers.map((t) => (
                <button
                  key={t}
                  onClick={() => { setTickerInput(t); submitTicker(t); }}
                  className="px-2.5 py-1 bg-slate-800/60 hover:bg-purple-500/20 border border-slate-700 hover:border-purple-400/40 text-slate-300 hover:text-purple-200 rounded-lg text-[11px] font-mono transition"
                  data-testid={`quick-ticker-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {effectiveTicker ? (
            <SmartAnalyzer
              key={effectiveTicker}
              ticker={effectiveTicker}
              onClose={closeAnalysis}
            />
          ) : (
            <EmptyState
              icon={Target}
              title="Entre un ticker pour démarrer"
              hint="Claude analyse la narrative news, les fondamentaux temps réel (yfinance) et te donne 4 scénarios de cible avec plan de trade (entrée / trim / stop)."
            />
          )}
        </div>
      )}

      {/* PORTFOLIO MODE */}
      {mode === "portfolio" && (
        <div className="space-y-4">
          {portfolio && portfolio.length > 0 ? (
            <SmartPortfolioAnalyzer portfolio={portfolio} owner={owner} />
          ) : (
            <EmptyState
              icon={Briefcase}
              title="Aucune position détectée"
              hint="Ajoute des positions dans l'onglet Portfolio pour pouvoir lancer un diagnostic Claude."
            />
          )}
        </div>
      )}

      {/* FINDER MODE */}
      {mode === "finder" && (
        <div className="space-y-4">
          <SmartFinderAnalyzer portfolio={portfolio} owner={owner} />
        </div>
      )}
    </div>
  );
});

function ModeButton({ active, onClick, icon: Icon, label, sub, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`p-3 sm:p-4 rounded-xl border text-left transition-all ${
        active
          ? "bg-purple-500/10 border-purple-400/40 shadow-glow"
          : "bg-slate-900/50 border-slate-700 hover:border-slate-500 hover:bg-slate-800/60"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${active ? "text-purple-300" : "text-slate-400"}`} />
        <span className={`text-xs sm:text-sm font-bold ${active ? "text-white" : "text-slate-200"}`}>
          {label}
        </span>
      </div>
      <p className="text-[10px] text-slate-500">{sub}</p>
    </button>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="glass-card rounded-xl p-10 sm:p-12 text-center border border-slate-700/60">
      <Icon className="w-14 h-14 text-slate-600 mx-auto mb-3" />
      <p className="text-slate-300 text-base font-semibold mb-2">{title}</p>
      <p className="text-slate-500 text-xs sm:text-sm max-w-md mx-auto">{hint}</p>
    </div>
  );
}
